import type { MarketplaceService } from '../../types/pricing';

export type GuidedModePath = 'standard_package' | 'help_me_choose';

export interface GuidedModeAnswers {
  path: GuidedModePath;
  supported_users: number;
  term_months: number;
  internal_it_owner?: 'yes' | 'no' | 'partial';
  productivity_platform?: string;
  security_baseline?: string;
  support_pain?: string[];
  site_selections?: Record<string, string>;
  site_connection_selections?: Record<string, Record<string, string>>;
}


export interface GuidedModeRecommendation {
  outcome: 'signature_support_standard_package' | 'needs_scoping_review' | 'consider_block_hours' | 'multi_location_quote_ready';
  title: string;
  summary: string;
  variantKey?: string;
  quantity: number;
  termMonths: number;
  monthlyCents?: number;
  reasons: string[];
  serviceConfiguration: Record<string, any>;
}

const NO_OPTION_KEY = '__none__';

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {};
}

function getOrderSchema(service: MarketplaceService): Record<string, any> {
  return asRecord((service as any).order_schema);
}

export function isGuidedCheckoutService(service: MarketplaceService): boolean {
  return getOrderSchema(service).mode === 'guided_checkout';
}

function getStandardPackage(service: MarketplaceService): Record<string, any> {
  return asRecord(asRecord(getOrderSchema(service).valid_options).standard_package);
}

function getGuidedRules(service: MarketplaceService): any[] {
  const rules = asRecord(getOrderSchema(service).valid_options).guided_rules;
  return Array.isArray(rules) ? rules : [];
}

function getGuidedQuestions(service: MarketplaceService): any[] {
  const questions = asRecord(getOrderSchema(service).valid_options).guided_questions;
  return Array.isArray(questions) ? questions : [];
}

function getMultiLocationQuote(service: MarketplaceService): Record<string, any> {
  return asRecord(asRecord(getOrderSchema(service).valid_options).multi_location_quote);
}

export function isMultiLocationGuidedQuoteService(service: MarketplaceService): boolean {
  return isGuidedCheckoutService(service) && Array.isArray(getMultiLocationQuote(service).sites);
}

export function getMultiLocationQuoteSpec(service: MarketplaceService): Record<string, any> {
  return getMultiLocationQuote(service);
}

function getSiteOptions(site: Record<string, any>): any[] {
  return Array.isArray(site.options) ? site.options : [];
}

function siteKey(site: Record<string, any>): string {
  return String(site.site_key || site.key || site.id || site.name || site.label || 'site');
}

function optionKey(option: Record<string, any>): string {
  return String(option.option_key || option.key || option.id || '');
}

function defaultOptionForSite(site: Record<string, any>): Record<string, any> | undefined {
  const options = getSiteOptions(site);
  return options.find((option) => option?.recommended === true) || options[0];
}

function getConnectionSlots(site: Record<string, any>): any[] {
  return Array.isArray(site.connection_slots) ? site.connection_slots : [];
}

function connectionSlotKey(slot: Record<string, any>, index: number): string {
  return String(slot.key || slot.slot_key || `provider_${index + 1}`);
}

function optionsForConnectionSlot(site: Record<string, any>, slot: Record<string, any>): Record<string, any>[] {
  const options = getSiteOptions(site).map(asRecord);
  const optionKeys = Array.isArray(slot.option_keys) ? slot.option_keys.map(String) : [];
  if (optionKeys.length === 0) return options;
  return optionKeys
    .map((key) => options.find((option) => optionKey(option) === key))
    .filter(Boolean) as Record<string, any>[];
}

function defaultOptionForConnectionSlot(site: Record<string, any>, slot: Record<string, any>): Record<string, any> | undefined {
  const options = optionsForConnectionSlot(site, slot);
  const defaultKey = String(slot.default_option_key || slot.recommended_option_key || '');
  return options.find((option) => optionKey(option) === defaultKey) || options.find((option) => option?.recommended === true) || options[0];
}

function optionMrcCents(option: Record<string, any>): number {
  return Math.max(0, Math.round(Number(option.mrc_cents || option.monthly_cents || 0)));
}

function optionNrcCents(option: Record<string, any>): number {
  return Math.max(0, Math.round(Number(option.nrc_cents || option.one_time_cents || 0)));
}

function optionLabelForQuote(option: Record<string, any>): string {
  const provider = option.provider || option.carrier || option.access_provider || 'Provider';
  const bandwidth = option.bandwidth_label || (option.bandwidth_mbps ? `${option.bandwidth_mbps}M` : 'Bandwidth');
  const accessType = option.access_type ? ` ${option.access_type}` : '';
  return `${provider} ${bandwidth}${accessType}`.trim();
}

export function getGuidedModeDefaults(service: MarketplaceService): GuidedModeAnswers {
  const multiLocationQuote = getMultiLocationQuote(service);
  if (Array.isArray(multiLocationQuote.sites)) {
    const siteSelections = multiLocationQuote.sites.reduce((acc: Record<string, string>, rawSite: any) => {
      const site = asRecord(rawSite);
      const key = siteKey(site);
      const selected = defaultOptionForSite(site);
      if (key && selected) acc[key] = optionKey(asRecord(selected));
      return acc;
    }, {});

    const siteConnectionSelections = multiLocationQuote.sites.reduce((acc: Record<string, Record<string, string>>, rawSite: any) => {
      const site = asRecord(rawSite);
      const key = siteKey(site);
      const slots = getConnectionSlots(site).map(asRecord);
      if (!key || slots.length === 0) return acc;
      acc[key] = slots.reduce((slotAcc: Record<string, string>, rawSlot: any, index: number) => {
        const slot = asRecord(rawSlot);
        const selected = defaultOptionForConnectionSlot(site, slot);
        if (selected) slotAcc[connectionSlotKey(slot, index)] = optionKey(asRecord(selected));
        return slotAcc;
      }, {});
      return acc;
    }, {});

    return {
      path: 'help_me_choose',
      supported_users: 1,
      term_months: Number(multiLocationQuote.term_months || service.default_term_months || 36),
      productivity_platform: 'not_applicable',
      internal_it_owner: 'partial',
      security_baseline: 'not_applicable',
      support_pain: [],
      site_selections: siteSelections,
      site_connection_selections: siteConnectionSelections,
    };
  }

  const standardPackage = getStandardPackage(service);
  return {
    path: 'standard_package',
    supported_users: Number(standardPackage.default_seats || 25),
    term_months: Number(standardPackage.default_term_months || service.default_term_months || 36),
    productivity_platform: 'microsoft_365',
    internal_it_owner: 'partial',
    security_baseline: 'managed',
    support_pain: [],
  };
}

function evaluateMultiLocationQuote(service: MarketplaceService, answers: GuidedModeAnswers): GuidedModeRecommendation {
  const quote = getMultiLocationQuote(service);
  const sites = Array.isArray(quote.sites) ? quote.sites.map(asRecord) : [];
  const termMonths = Number(quote.term_months || answers.term_months || service.default_term_months || 36);
  const selectedSites = sites.map((site) => {
    const key = siteKey(site);
    const options = getSiteOptions(site).map(asRecord);
    const connectionSlots = getConnectionSlots(site).map(asRecord);

    if (connectionSlots.length > 0) {
      const selectedConnections = connectionSlots.map((slot, index) => {
        const slotKey = connectionSlotKey(slot, index);
        const slotOptions = optionsForConnectionSlot(site, slot);
        const requestedOptionKey = key ? answers.site_connection_selections?.[key]?.[slotKey] : undefined;
        if (requestedOptionKey === NO_OPTION_KEY) return null;
        const selectedOption = slotOptions.find((option) => optionKey(option) === requestedOptionKey) || defaultOptionForConnectionSlot(site, slot) || slotOptions[0] || {};
        const mrcCents = optionMrcCents(selectedOption);
        const nrcCents = optionNrcCents(selectedOption);

        return {
          slot_key: slotKey,
          slot_label: slot.label || `Provider ${index + 1}`,
          default_role: slot.default_role || (index === 0 ? 'Primary' : 'Secondary'),
          option_key: optionKey(selectedOption),
          option_label: optionLabelForQuote(selectedOption),
          provider: selectedOption.provider || selectedOption.carrier || selectedOption.access_provider,
          access_type: selectedOption.access_type || 'DIA',
          bandwidth_mbps: selectedOption.bandwidth_mbps,
          bandwidth_label: selectedOption.bandwidth_label || (selectedOption.bandwidth_mbps ? `${selectedOption.bandwidth_mbps}M` : undefined),
          mrc_cents: mrcCents,
          nrc_cents: nrcCents,
          included_equipment: selectedOption.included_equipment || selectedOption.includes || [],
          mrc_components_cents: selectedOption.mrc_components_cents || {},
          one_time_components_cents: selectedOption.one_time_components_cents || selectedOption.nrc_components_cents || {},
          add_ons: selectedOption.add_ons || selectedOption.addons || [],
          caveats: selectedOption.caveats || [],
          recommended: selectedOption.recommended === true || optionKey(selectedOption) === String(slot.default_option_key || ''),
        };
      }).filter(Boolean) as Array<Record<string, any>>;
      const mrcCents = selectedConnections.reduce((sum, connection) => sum + connection.mrc_cents, 0);
      const nrcCents = selectedConnections.reduce((sum, connection) => sum + connection.nrc_cents, 0);

      return {
        site_key: key,
        site_name: site.name || site.label || key,
        address: site.address,
        connection_mode: 'dual_connection',
        connections: selectedConnections,
        option_key: selectedConnections.map((connection) => connection.option_key).join('+'),
        option_label: selectedConnections.map((connection) => `${connection.slot_label}: ${connection.option_label}`).join(' + '),
        provider: selectedConnections.map((connection) => connection.provider).filter(Boolean).join(' + '),
        access_type: 'Diverse access paths',
        bandwidth_label: selectedConnections.map((connection) => connection.bandwidth_label).filter(Boolean).join(' + '),
        mrc_cents: mrcCents,
        nrc_cents: nrcCents,
        included_equipment: Array.from(new Set(selectedConnections.flatMap((connection) => connection.included_equipment || []))),
        mrc_components_cents: selectedConnections.reduce((acc: Record<string, number>, connection) => ({ ...acc, [connection.slot_key]: connection.mrc_cents }), {}),
        one_time_components_cents: selectedConnections.reduce((acc: Record<string, number>, connection) => ({ ...acc, [connection.slot_key]: connection.nrc_cents }), {}),
        add_ons: selectedConnections.flatMap((connection) => connection.add_ons || []),
        caveats: selectedConnections.flatMap((connection) => connection.caveats || []),
        recommended: selectedConnections.every((connection) => connection.recommended),
      };
    }

    const requestedOptionKey = key ? answers.site_selections?.[key] : undefined;
    const selectedOption = options.find((option) => optionKey(option) === requestedOptionKey) || defaultOptionForSite(site) || options[0] || {};
    const mrcCents = optionMrcCents(selectedOption);
    const nrcCents = optionNrcCents(selectedOption);

    return {
      site_key: key,
      site_name: site.name || site.label || key,
      address: site.address,
      option_key: optionKey(selectedOption),
      option_label: optionLabelForQuote(selectedOption),
      provider: selectedOption.provider || selectedOption.carrier || selectedOption.access_provider,
      access_type: selectedOption.access_type || 'DIA',
      bandwidth_mbps: selectedOption.bandwidth_mbps,
      bandwidth_label: selectedOption.bandwidth_label || (selectedOption.bandwidth_mbps ? `${selectedOption.bandwidth_mbps}M` : undefined),
      mrc_cents: mrcCents,
      nrc_cents: nrcCents,
      included_equipment: selectedOption.included_equipment || selectedOption.includes || [],
      mrc_components_cents: selectedOption.mrc_components_cents || {},
      one_time_components_cents: selectedOption.one_time_components_cents || selectedOption.nrc_components_cents || {},
      add_ons: selectedOption.add_ons || selectedOption.addons || [],
      caveats: selectedOption.caveats || [],
      recommended: selectedOption.recommended === true,
    };
  }).filter((site) => Number(site.mrc_cents || 0) > 0 || Number(site.nrc_cents || 0) > 0 || (Array.isArray((site as any).connections) && (site as any).connections.length > 0));

  const selectedConnectionCount = selectedSites.reduce((sum, site: any) => sum + (Array.isArray(site.connections) ? site.connections.length : 1), 0);
  const monthlyCents = selectedSites.reduce((sum, site) => sum + site.mrc_cents, 0);
  const nrcCents = selectedSites.reduce((sum, site) => sum + site.nrc_cents, 0);
  const quoteName = String(quote.name || `${quote.supplier_name || service.brand_name || service.name} multi-location quote`);
  const title = selectedConnectionCount > 0
    ? `${quote.supplier_name || service.brand_name || 'Provider'} ${selectedSites.length}-site quote ready`
    : 'Choose at least one circuit';
  const summary = selectedConnectionCount > 0
    ? `${selectedSites.length} sites / ${selectedConnectionCount} circuits configured as one managed connectivity order. Estimated MRC is $${(monthlyCents / 100).toLocaleString('en-US')} with $${(nrcCents / 100).toLocaleString('en-US')} NRC, subject to facility validation.`
    : 'Select at least one provider circuit at any site before adding this quote to cart.';

  return {
    outcome: selectedConnectionCount > 0 ? 'multi_location_quote_ready' : 'needs_scoping_review',
    title,
    summary,
    quantity: 1,
    termMonths,
    monthlyCents,
    reasons: [
      'Configured as one BCN-managed service with two diverse connections per site.',
      'Underlying access providers are captured per site without creating separate cart items.',
      'Final supplier submission can validate facilities/site survey details after checkout.',
    ],
    serviceConfiguration: {
      guided_mode: {
        entry: answers.path,
        outcome: 'multi_location_quote_ready',
        quote_type: 'multi_location_connectivity',
        term_months: termMonths,
        recommendation_title: title,
        recommendation_summary: summary,
      },
      multi_location_quote: {
        quote_key: quote.quote_key || quote.key || 'multi-location-connectivity',
        quote_name: quoteName,
        supplier_name: quote.supplier_name || service.brand_name,
        customer_name: quote.customer_name,
        term_months: termMonths,
        currency: quote.currency || 'USD',
        site_count: selectedSites.length,
        selected_circuit_count: selectedConnectionCount,
        total_mrc_cents: monthlyCents,
        total_nrc_cents: nrcCents,
        sites: selectedSites,
        assumptions: quote.assumptions || [],
      },
    },
  };
}

function findVariantKey(service: MarketplaceService, seats: number, termMonths: number): string | undefined {
  const variants = service.variants?.variants || [];
  const termText = `${termMonths}`;
  const tier = variants.find((variant: any) => {
    const key = String(variant.key || variant.variant_key || '').toLowerCase();
    const label = `${variant.display_name || ''} ${variant.attribute1_value || ''} ${variant.attribute2_value || ''}`.toLowerCase();
    const matchesTerm = key.includes(termText) || label.includes(termText);
    if (!matchesTerm) return false;
    if (seats <= 25) return key.includes('1-25') || label.includes('1-25');
    if (seats <= 50) return key.includes('26-50') || label.includes('26-50');
    return key.includes('51-100') || label.includes('51-100');
  });
  return tier?.variant_key || tier?.key || service.default_variant_key;
}

function monthlyCentsFor(service: MarketplaceService, variantKey: string | undefined, seats: number): number | undefined {
  if (!variantKey) return undefined;
  const variant = (service.variants?.variants || []).find((entry: any) => (entry.variant_key || entry.key) === variantKey);
  const recurring = Number(variant?.pricing?.recurring_cents || 0);
  return recurring > 0 ? recurring * seats : undefined;
}

function answerMatches(expected: any, actual: any): boolean {
  if (expected && typeof expected === 'object' && !Array.isArray(expected)) {
    const numericActual = Number(actual);
    if (typeof expected.gt === 'number' && !(numericActual > expected.gt)) return false;
    if (typeof expected.gte === 'number' && !(numericActual >= expected.gte)) return false;
    if (typeof expected.lt === 'number' && !(numericActual < expected.lt)) return false;
    if (typeof expected.lte === 'number' && !(numericActual <= expected.lte)) return false;
    if (typeof expected.max_selected === 'number') {
      const selected = Array.isArray(actual) ? actual.length : actual ? 1 : 0;
      if (selected > expected.max_selected) return false;
    }
    if (Array.isArray(expected.includes_any)) {
      const selected = Array.isArray(actual) ? actual : [actual];
      if (!expected.includes_any.some((value: any) => selected.includes(value))) return false;
    }
    if (Object.prototype.hasOwnProperty.call(expected, 'equals') && actual !== expected.equals) return false;
    return true;
  }

  if (Array.isArray(expected)) {
    const selected = Array.isArray(actual) ? actual : [actual];
    return expected.every((value) => selected.includes(value));
  }

  return actual === expected;
}

function ruleMatches(rule: any, answers: Record<string, any>): boolean {
  const match = asRecord(rule?.match);
  return Object.entries(match).every(([key, expected]) => answerMatches(expected, answers[key]));
}

function applyGuidedRules(service: MarketplaceService, answers: Record<string, any>): Partial<GuidedModeRecommendation> & { appliedReasons?: string[] } {
  const rules = getGuidedRules(service);
  if (rules.length === 0) return {};

  const result: Partial<GuidedModeRecommendation> & { appliedReasons?: string[] } = { appliedReasons: [] };
  for (const rule of rules) {
    if (!ruleMatches(rule, answers)) continue;

    if (rule.outcome) result.outcome = rule.outcome;
    if (rule.title) result.title = rule.title;
    if (rule.summary) result.summary = rule.summary;

    const apply = asRecord(rule.apply);
    if (typeof apply.variant_key === 'string') result.variantKey = apply.variant_key;
    if (typeof apply.term_months === 'number') result.termMonths = apply.term_months;
    if (typeof apply.quantity === 'number') result.quantity = apply.quantity;
    if (typeof apply.quantity_from === 'string') {
      const quantity = Number(answers[apply.quantity_from]);
      if (Number.isFinite(quantity) && quantity > 0) result.quantity = Math.floor(quantity);
    }
    if (rule.reason) result.appliedReasons?.push(rule.reason);
  }

  return result;
}

export function evaluateGuidedMode(service: MarketplaceService, answers: GuidedModeAnswers): GuidedModeRecommendation {
  if (isMultiLocationGuidedQuoteService(service)) {
    return evaluateMultiLocationQuote(service, answers);
  }

  const seats = Math.max(1, Math.floor(Number(answers.supported_users || 1)));
  const termMonths = Number(answers.term_months || service.default_term_months || 36);
  const reasons: string[] = [];

  let outcome: GuidedModeRecommendation['outcome'] = 'signature_support_standard_package';
  let title = 'Signature Support recommended';
  let summary = 'Use Ntiva Signature Support as the managed IT operating model, then confirm environment details after checkout.';

  if (seats > 100) {
    outcome = 'needs_scoping_review';
    title = 'Needs scoping review';
    summary = 'This looks larger than the published package range. Capture the request, then finalize the managed-services design with Ntiva before supplier submission.';
    reasons.push('Supported user count is above the published 100-user package range.');
  } else if (answers.internal_it_owner === 'yes' && (answers.support_pain || []).length <= 1) {
    outcome = 'consider_block_hours';
    title = 'Consider Block Hours first';
    summary = 'The buyer may already have enough internal IT ownership for a reserved-capacity support model. Signature Support still works if they want Ntiva to own day-to-day operations.';
    reasons.push('Buyer reports an internal IT owner and limited pain points.');
  } else {
    reasons.push('Buyer needs a recurring managed IT operating model rather than one-off support.');
  }

  if (answers.security_baseline === 'basic' || answers.security_baseline === 'not_sure') {
    reasons.push('Security baseline suggests managed endpoint, identity, and backup controls are valuable.');
  }
  if ((answers.support_pain || []).includes('unclear_ownership')) {
    reasons.push('Unclear ownership is a strong fit for one accountable MSP.');
  }

  const ruleResult = applyGuidedRules(service, answers as unknown as Record<string, any>);
  outcome = ruleResult.outcome || outcome;
  title = ruleResult.title || title;
  summary = ruleResult.summary || summary;
  if (ruleResult.appliedReasons?.length) {
    reasons.unshift(...ruleResult.appliedReasons);
  }

  const effectiveSeats = Math.max(1, Math.floor(Number(ruleResult.quantity || seats)));
  const effectiveTermMonths = Number(ruleResult.termMonths || termMonths);
  const variantKey = ruleResult.variantKey || findVariantKey(service, effectiveSeats, effectiveTermMonths);
  const monthlyCents = monthlyCentsFor(service, variantKey, effectiveSeats);

  return {
    outcome,
    title,
    summary,
    variantKey,
    quantity: effectiveSeats,
    termMonths: effectiveTermMonths,
    monthlyCents,
    reasons,
    serviceConfiguration: {
      guided_mode: {
        entry: answers.path,
        outcome,
        supported_users: effectiveSeats,
        term_months: effectiveTermMonths,
        internal_it_owner: answers.internal_it_owner,
        productivity_platform: answers.productivity_platform,
        security_baseline: answers.security_baseline,
        support_pain: answers.support_pain || [],
        recommendation_title: title,
        recommendation_summary: summary,
      },
    },
  };
}

export function getGuidedQuestionsForDisplay(service: MarketplaceService): any[] {
  return getGuidedQuestions(service);
}
